import React, { useCallback, useState } from 'react';
import useCompanyContract from '../hooks/use-company-contract';
import { useParams } from 'react-router-dom';
import { PageParams } from '../types/PageParams';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Select } from 'src/components/Form/Select';
import {
  Button,
  ButtonVariant,
  FaIcons,
  Notification,
  SelectOptionProps,
  Theme,
} from '@innovationnorway/component-library';
import Input from 'src/components/Form/Input';
import { formatDateToNOString } from 'src/utils/FormatValue';
import { capitalizeFirstLetter } from 'src/utils/string';
import useToggle from 'src/hooks/use-toggle';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import ErrorSupportMessage from 'src/features/siva/components/ErrorSupportMessage';
import { requiredI18n } from 'src/utils/validation';
import { convertIdToNumber } from '../utils/idConversion';
import { SivaContractUpdateDto, SivaPhase } from 'src/api/v2';
import { bransjeOptions, ideaSourceOptions } from '../../constants';
import useSivaAdvisorOptions from '../../hooks/use-siva-advisor-options';
import '../styles/Contract.scss';
import { Link } from 'react-router-dom';
import { useSupportEmail } from 'src/hooks/use-support-email';
import ConnectCompany from './ConnectCompany';
import useGeonorge, { Municipality } from 'src/features/one-way-in/hooks/use-geonorge';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import ValueField from 'src/components/ValueField/ValueField';
import TertiaryButton from 'src/components/TertiaryButton/TertiaryButton';
import { useAuth } from 'src/features/authorization';
import { Textarea, Label } from '@innovationnorway/cl-react';
import '../styles/Contract.scss';

type Props = {
  onCancel: () => void;
};

const EditContract: React.FC<Props> = ({ onCancel }) => {
  const { contractId } = useParams<PageParams>();
  const {
    contract,
    contractAdmissionDate,
    currentPhase,
    admissionFormDate,
    enrouteFormDate,
    exitFormDate,
    updateSivaContractMutation,
    company,
  } = useCompanyContract(convertIdToNumber(contractId));
  const { checkAccess } = useAuth();
  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  // Dette gjør at vi kan refetch rådgivere i et intervall, etter at bruker har trykt på linken for Tilgangsstyring
  const [refetchInterval, setRefetchInterval] = useState(0);
  const REFETCH_INTERVAL = 1000 * 15;
  const startRefetchInterval = useCallback(() => setRefetchInterval(REFETCH_INTERVAL), [REFETCH_INTERVAL]);
  const { advisorOptions } = useSivaAdvisorOptions({
    refetchInterval,
  });
  const { municipalities, getMunicipalityNameByNumber } = useGeonorge();

  const [companyError, setCompanyError] = useState(false);
  const [orgNumber, setOrgNumber] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');

  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');
  const { t: tError } = useTranslation('error');
  const [updateError, setUpdateError] = useState(false);

  const { isActive, activate, deactivate } = useToggle(false);
  const { email, mailto } = useSupportEmail();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<SivaContractUpdateDto>({
    shouldFocusError: true,
    defaultValues: {
      ...contract,
    },
  });

  const onSubmit = handleSubmit(async (updateDto: SivaContractUpdateDto) => {
    await updateSivaContractMutation
      .mutateAsync({
        ...updateDto,
        organizationNumber: orgNumber,
      })
      .then(() => {
        setUpdateError(false);
      })
      .catch(() => {
        setUpdateError(true);
      })
      .finally(() => {
        activate();
      });
  });

  return (
    <div className="width--60">
      <h2 className="margin-bottom--5">{tSiva('company.details.contract.editContract')}</h2>

      <form onSubmit={onSubmit} className="stack">
        <div>
          {currentPhase?.sivaPhase === SivaPhase.Idea ? (
            <h3>{tSiva('idea.description')}</h3>
          ) : (
            <h3>{tSiva('newCompany.contract.section.info.title')}</h3>
          )}

          {!company && (
            <div>
              <span className="font-weight--bold">{tSiva('contract.edit.connectCompany')}</span>
              {currentPhase && (
                <ConnectCompany
                  phase={currentPhase.sivaPhase}
                  selectedCompanyName={selectedCompanyName}
                  orgNumber={orgNumber}
                  setOrgNumber={setOrgNumber}
                  setSelectedCompanyName={setSelectedCompanyName}
                  setCompanyError={setCompanyError}
                  showNoCompanyCheckbox={true}
                />
              )}
            </div>
          )}

          <div>
            <span className="font-weight--bold">{tCommon('phase')}</span>
          </div>
          <div>
            {tSiva(`phase.${currentPhase?.sivaPhase}` as any, {
              defaultValue: currentPhase?.sivaPhase || '',
            })}
          </div>
        </div>
        <div>
          <Select
            label={`${tCommon('industry')} ${currentPhase?.sivaPhase === SivaPhase.Idea ? '' : '*'}`}
            {...register('externalIndustry', {
              required: currentPhase?.sivaPhase === SivaPhase.Idea ? undefined : requiredI18n(tError),
            })}
            error={errors?.externalIndustry?.message}
            options={bransjeOptions}
          />
        </div>
        <div>
          <Input
            label={`${tSiva('company.details.contract.overview.ideaName')} *`}
            {...register('name', { required: requiredI18n(tError) })}
            error={errors?.name?.message}
          />
        </div>
        <div>
          <Select
            label={`${tSiva('company.details.contract.overview.ideaSource')} *`}
            {...register('ideaSource', {
              required: requiredI18n(tError),
            })}
            error={errors?.ideaSource?.message}
            options={ideaSourceOptions}
          />
        </div>
        {contract?.phases.at(-1)?.sivaPhase !== SivaPhase.Idea && (
          <>
            <div className="margin-top--6 ">
              <Notification type="generic" fullWidth>
                {`${tSiva('company.details.contract.editContract.admissionNote')} `}
                <a className="siva-contract-overview__back-link" href={mailto} title="support@siva.no">
                  {email}
                </a>
              </Notification>
            </div>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.admissionDate')}
                </span>
              </div>
              <div>
                {contractAdmissionDate &&
                  formatDateToNOString(new Date(contractAdmissionDate), false, '{date}.{month}.{year}', true)}
              </div>
            </div>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.dispatchAdmissionForm')}
                </span>
              </div>
              <div>
                {!!admissionFormDate &&
                  capitalizeFirstLetter(
                    formatDateToNOString(new Date(admissionFormDate), true, '{month} {year}', true),
                  )}
              </div>
            </div>
            <div>
              <div>
                <span className="font-weight--bold">
                  {tSiva('company.details.contract.overview.dispatchEnRouteForm')}
                </span>
              </div>
              <div>
                {!!enrouteFormDate &&
                  capitalizeFirstLetter(
                    formatDateToNOString(new Date(enrouteFormDate), true, '{month} {year}', true),
                  )}
              </div>
            </div>
          </>
        )}
        {!!exitFormDate && (
          <div>
            <div>
              <span className="font-weight--bold">
                {tSiva('company.details.contract.overview.dispatchExitForm')}
              </span>
            </div>
            <div>
              {capitalizeFirstLetter(
                formatDateToNOString(new Date(exitFormDate), true, '{month} {year}', true),
              )}
            </div>
          </div>
        )}
        <div className="margin-top--6">
          <h3>{tCommon('contact')}</h3>
          <p>{tSiva('newCompany.contract.section.contact.description')}</p>
        </div>
        <div className="display--flex gap--4 justify-content--space-between">
          <div>
            <Input
              label={`${tCommon('firstName')} *`}
              {...register('contactFirstName', { required: requiredI18n(tError) })}
              error={errors?.name?.message}
            />
          </div>
          <div>
            <Input
              label={`${tCommon('lastName')} *`}
              {...register('contactLastName', { required: requiredI18n(tError) })}
              error={errors?.name?.message}
            />
          </div>
        </div>
        <div>
          <Input
            label={`${tCommon('email')} *`}
            {...register('contactEmail', { required: requiredI18n(tError) })}
            error={errors?.name?.message}
          />
        </div>
        {!isBusinessGarden && currentPhase?.sivaPhase === SivaPhase.Idea && (
          <>
            {watch('municipality') === undefined ? (
              <Controller
                control={control}
                name="municipality"
                render={({ field }) => (
                  <Autocomplete<Municipality>
                    label={tSiva('newCompany.contract.form.municipality.label')}
                    name={field.name}
                    items={municipalities.sort((a, b) => a.name.localeCompare(b.name))}
                    filter={(item, value: string): boolean => {
                      const lowerCaseValue = value.toLowerCase();

                      return item.name.toLowerCase().includes(lowerCaseValue) || false;
                    }}
                    onItemSelected={(muni) => field.onChange(muni.number)}
                    itemLabel={(muni) => muni.name}
                    renderItem={(item) => <span>{item.name}</span>}
                  />
                )}
              />
            ) : (
              <ValueField
                label={tSiva('newCompany.contract.form.municipality.label')}
                value={
                  <div className="display--flex gap--2 align-items--center">
                    <span>{getMunicipalityNameByNumber(watch('municipality'))}</span>
                    <TertiaryButton
                      ariaLabel={`${tCommon('edit')} ${tCommon('municipality').toLowerCase()}`}
                      faIcon={FaIcons.EditLight}
                      minimalPadding
                      onClick={() => setValue('municipality', undefined)}
                    />
                  </div>
                }
              />
            )}
          </>
        )}
        <div className="margin-top--6 ">
          <h3>{tSiva('newCompany.contract.section.followUp.title')}</h3>
          <Controller
            control={control}
            name="advisorId"
            render={({ field }) => (
              <Autocomplete<SelectOptionProps>
                label={tCommon('advisor')}
                name={field.name}
                items={advisorOptions}
                filter={(item, value: string): boolean => {
                  const lowerCaseValue = value.toLowerCase();

                  return item.text.toLowerCase().includes(lowerCaseValue) || false;
                }}
                onItemSelected={(advisor) => field.onChange(advisor.value)}
                itemLabel={(advisor) => `${advisor.text}`}
                renderItem={(advisor) => `${advisor.text}`}
                defaultValue={advisorOptions.find((x) => x.value === field.value)?.text}
              />
            )}
          />
          <p className="margin-top--0 margin-bottom--4">
            {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
            <Link
              to={`/actor-profile/settings/access`}
              target="_blank"
              onClick={() => {
                startRefetchInterval();
              }}
            >
              <span>{tCommon('accessControl')}</span>
            </Link>
            .
          </p>
        </div>
        <div className="font-size--x-large">
          <Label htmlFor="note" weight="semibold">
            {tCommon('note')}
          </Label>
          <Textarea id="note" {...register('notes')} maxLength={500} rows={4} />
        </div>
        <div className="display--flex gap--1 justify-content--flex-start margin-top--7">
          <Button
            theme={Theme.Neutral}
            type="submit"
            isLoading={updateSivaContractMutation.isLoading}
            disabled={updateSivaContractMutation.isLoading || companyError}
          >
            {tCommon('save')}
          </Button>
          <Button
            theme={Theme.Neutral}
            variant={ButtonVariant.Outlined}
            type="button"
            onClick={onCancel}
            isLoading={updateSivaContractMutation.isLoading}
            disabled={updateSivaContractMutation.isLoading}
          >
            {tCommon('cancel')}
          </Button>
        </div>
      </form>
      <DialogContainer
        title={
          updateError
            ? tSiva('company.details.contract.editContract.errormodal.header')
            : tCommon('confirmation')
        }
        show={isActive}
        onClose={deactivate}
      >
        {!updateError && <div>{tSiva('company.details.contract.editContract.successmodal.text')}</div>}
        {!!updateError && <ErrorSupportMessage />}
        <Button className="margin-top--6" theme={Theme.Neutral} type="button" onClick={() => deactivate()}>
          {tCommon('close')}
        </Button>
      </DialogContainer>
    </div>
  );
};

export default EditContract;
